import { useMemo, useState } from 'react';
import { categories, categoryArticles } from '@/mocks/news';

export default function CategoryFeed() {
  const [active, setActive] = useState(categories[0]);
  const [query, setQuery] = useState('');

  const allArticles = useMemo(
    () => categories.flatMap((c) => categoryArticles[c]),
    [],
  );

  const list = useMemo(() => {
    const kw = query.trim().toLowerCase();
    if (!kw) return categoryArticles[active];
    return allArticles.filter(
      (a) => a.title.toLowerCase().includes(kw) || a.summary.toLowerCase().includes(kw) || a.tag.includes(kw),
    );
  }, [query, active, allArticles]);

  return (
    <section id="feed" className="scroll-mt-16">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-bold tracking-widest text-primary-700">
              <span className="h-px w-8 bg-primary-500" /> 分类快讯 · 按栏目刷新
            </p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              栏目里的新鲜事
            </h2>
          </div>

          {/* 搜索框 */}
          <div className="relative w-full lg:w-80">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-foreground-500"
              aria-hidden="true"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索栏目内的话题…"
              aria-label="站内搜索"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        {/* 分类切换 */}
        {!query && (
          <div
            role="tablist"
            aria-label="栏目切换"
            className="mt-6 flex w-fit flex-wrap gap-1 rounded-full bg-background-200/80 p-1"
          >
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={active === c}
                onClick={() => setActive(c)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === c
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:text-primary-700'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {query && (
          <p className="mt-5 text-sm text-foreground-600">
            找到 <span className="font-bold text-primary-700">{list.length}</span> 条与
            “{query}” 相关的内容
          </p>
        )}

        {/* 卡片流 */}
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((a) => (
            <article
              key={a.id}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={a.image}
                  alt={a.title}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 whitespace-nowrap rounded-md bg-foreground-950/70 px-2.5 py-1 text-xs font-semibold text-background-50 backdrop-blur-sm">
                  {a.tag}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <div className="flex items-center gap-2 text-xs text-foreground-600">
                  <span className="font-semibold text-primary-700">{a.category}</span>
                  <span>·</span>
                  <span>{a.time}</span>
                </div>
                <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                  {a.title}
                </h3>
                <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-700">
                  {a.summary}
                </p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-600">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-3-line" aria-hidden="true" /> {a.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" aria-hidden="true" /> {a.read}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {list.length === 0 && (
          <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100/60 py-14 text-center">
            <i className="ri-inbox-2-line text-4xl text-foreground-400" aria-hidden="true" />
            <p className="mt-3 text-sm text-foreground-600">没有找到相关内容，换个关键词试试</p>
          </div>
        )}
      </div>
    </section>
  );
}